import { Suspense, lazy } from 'react'
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { LanguageProvider } from './contexts/LanguageContext'
import Header from './components/layout/Header'
import Footer from './components/layout/Footer'
import GoToTop from './components/GoToTop'
import ContactWidget from './components/ContactWidget'
import ScrollToTop from './components/ScrollToTop'
import { 
  PageSkeleton, 
  HomeSkeleton, 
  CSRSkeleton, 
  PolicyPageSkeleton, 
  LogoGridPageSkeleton,
  ServicePageSkeleton,
  SectorPageSkeleton,
  ContactPageSkeleton,
  BlogPageSkeleton,
  SingleBlogPostSkeleton,
  SingleProjectSkeleton,
  AboutPageSkeleton,
  ListingPageSkeleton,
  ProjectsPageSkeleton,
  CareerDocumentPageSkeleton,
  OrganizationChartSkeleton,
  LegalPageSkeleton
} from './components/Skeleton'
import AdminSkeleton from './components/admin/AdminSkeleton'

// Lazy load pages
const Home = lazy(() => import('./pages/Home'))
const About = lazy(() => import('./pages/About'))
const BuildingInfrastructure = lazy(() => import('./pages/services/BuildingInfrastructure'))
const DesignEngineering = lazy(() => import('./pages/services/DesignEngineering'))
const ArchitecturalFitout = lazy(() => import('./pages/services/ArchitecturalFitout'))
const CivilWorks = lazy(() => import('./pages/services/CivilWorks'))
const MEPWorks = lazy(() => import('./pages/services/MEPWorks'))
const DataCenters = lazy(() => import('./pages/services/DataCenters'))
const Pricing = lazy(() => import('./pages/services/Pricing'))
const Maintenance = lazy(() => import('./pages/services/Maintenance'))
const Sectors = lazy(() => import('./pages/Sectors'))
const Commercial = lazy(() => import('./pages/sectors/Commercial'))
const Entertainment = lazy(() => import('./pages/sectors/Entertainment'))
const Hotel = lazy(() => import('./pages/sectors/Hotel'))
const Industrial = lazy(() => import('./pages/sectors/Industrial'))
const Residential = lazy(() => import('./pages/sectors/Residential'))
const Education = lazy(() => import('./pages/sectors/School'))
const Utilities = lazy(() => import('./pages/sectors/Utilities'))
const Warehouse = lazy(() => import('./pages/sectors/Warehouse'))
const Projects = lazy(() => import('./pages/Projects'))
const Contact = lazy(() => import('./pages/Contact'))
const Documents = lazy(() => import('./pages/Documents'))
const Blog = lazy(() => import('./pages/Blog'))
// Individual blog post pages
const SmartCitiesFutureUrbanInfrastructure = lazy(() => import('./pages/blog/smart-cities-future-urban-infrastructure'))
const SustainableConstructionBuildingTomorrow = lazy(() => import('./pages/blog/sustainable-construction-building-tomorrow'))
const ModernBridgeEngineeringConceptCompletion = lazy(() => import('./pages/blog/modern-bridge-engineering-concept-completion'))
const RisePrefabricatedConstruction = lazy(() => import('./pages/blog/rise-prefabricated-construction'))
const SafetyFirstAdvancedConstructionProtocols = lazy(() => import('./pages/blog/safety-first-advanced-construction-protocols'))
const GreenBuildingMaterialsEcoFriendlyAlternatives = lazy(() => import('./pages/blog/green-building-materials-eco-friendly-alternatives'))
const Careers = lazy(() => import('./pages/Careers'))
const OurSuccessPartners = lazy(() => import('./pages/OurSuccessPartners'))
const OurVendors = lazy(() => import('./pages/OurVendors'))
const OrganizationChart = lazy(() => import('./pages/OrganizationChart'))
const CSR = lazy(() => import('./pages/CSR'))
const CodeOfEthics = lazy(() => import('./pages/CodeOfEthics'))
const HSE = lazy(() => import('./pages/HSE'))
const QualityManagement = lazy(() => import('./pages/QualityManagement'))
const OurServices = lazy(() => import('./pages/OurServices'))
const IndustriesSectors = lazy(() => import('./pages/IndustriesSectors'))
const Clients = lazy(() => import('./pages/Clients'))

const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'))
const TermsOfService = lazy(() => import('./pages/TermsOfService'))
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'))
const SiteMap = lazy(() => import('./pages/SiteMap'))

// Project pages (static)
const Alesayibeverageskhumrahproject = lazy(() => import('./pages/projects/alesayibeverageskhumrahproject'))
const Alesayibeveragesphase2industrialcity2project = lazy(() => import('./pages/projects/alesayibeveragesphase2industrialcity2project'))
const Madinahdepotutilitiesharamainhighspeedrailwayproject = lazy(() => import('./pages/projects/madinahdepotutilitiesharamainhighspeedrailwayproject'))
const Aatcofoodfactoryproject = lazy(() => import('./pages/projects/aatcofoodfactoryproject'))
const Cigalapharmawarehouseproject = lazy(() => import('./pages/projects/cigalapharmawarehouseproject'))
const Imasigmawarehouseinternationalmaritimeagencyproject = lazy(() => import('./pages/projects/imasigmawarehouseinternationalmaritimeagencyproject'))
const Sheikhammaryousefnaghivillaproject = lazy(() => import('./pages/projects/sheikhammaryousefnaghivillaproject'))
const Minidepotharamainhighspeedrailwayprojectsabber = lazy(() => import('./pages/projects/minidepotharamainhighspeedrailwayprojectsabber'))
const Workbase01haramainhighspeedrailwayjeddahproject = lazy(() => import('./pages/projects/workbase01haramainhighspeedrailwayjeddahproject'))
const Depotutilitiesharamainhighspeedrailwaymakkahproject = lazy(() => import('./pages/projects/depotutilitiesharamainhighspeedrailwaymakkahproject'))
const Kingabudullaheconomiccitywarehouseparkrabighproject = lazy(() => import('./pages/projects/kingabudullaheconomiccitywarehouseparkrabighproject'))
const Empirecinemarashidmallproject = lazy(() => import('./pages/projects/empirecinemarashidmallproject'))
const Voxcinemastownsqujameaplzajeddahproject = lazy(() => import('./pages/projects/voxcinemastownsqujameaplzajeddahproject'))
const Neomsoundstageproject = lazy(() => import('./pages/projects/neomsoundstageproject'))
const Neomsoundstagephase2project = lazy(() => import('./pages/projects/neomsoundstagephase2project'))
const Sheikhahmedbinmahfouzvillaproject = lazy(() => import('./pages/projects/sheikhahmedbinmahfouzvillaproject'))
const Delmonexpansionproject = lazy(() => import('./pages/projects/delmonexpansionproject'))
const Altamimwarehouseproject = lazy(() => import('./pages/projects/altamimwarehouseproject'))
const Hhsrfitoutsprojects = lazy(() => import('./pages/projects/hhsrfitoutsprojects'))
const Grandcinemaobhurmalljeddahproject = lazy(() => import('./pages/projects/grandcinemaobhurmalljeddahproject'))
const Princefaisalbinmogrentajjeddahproject = lazy(() => import('./pages/projects/princefaisalbinmogrentajjeddahproject'))
const Empirecinemaothaimmallalahsaproject = lazy(() => import('./pages/projects/empirecinemaothaimmallalahsaproject'))
const Alyamamawindowtoweryanbuproject = lazy(() => import('./pages/projects/alyamamawindowtoweryanbuproject'))
const Tamimikairwandammamcoreandshellproject = lazy(() => import('./pages/projects/tamimikairwandammamcoreandshellproject'))
const Thefilmstudiosandsupportingbuildingforrcuphase1project = lazy(() => import('./pages/projects/thefilmstudiosandsupportingbuildingforrcuphase1project'))
const Rehabilitationofsheikhsaeedabdaldaimmosqueforrcuproject = lazy(() => import('./pages/projects/rehabilitationofsheikhsaeedabdaldaimmosqueforrcuproject'))
const Empirecinemasatothaimmallinriyadhproject = lazy(() => import('./pages/projects/empirecinemasathaimmallinriyadhproject'))
const Grandcinemasatalmamshamallinalahsaproject = lazy(() => import('./pages/projects/grandcinemasatmamshamallinalahsaproject'))
const Fibercementboardfactoryproject = lazy(() => import('./pages/projects/fibercementboardfactoryproject'))
const Ayanindustrialcompanymodulinesproject = lazy(() => import('./pages/projects/ayanindustrialcompanymodulinesproject'))
const Southernpackfactorykaecproject = lazy(() => import('./pages/projects/southernpackfactorykaecproject'))
const Empirecinemaatalrachidmegamallinmadinahproject = lazy(() => import('./pages/projects/empirecinemaatalrachidmegamallinmadinahproject'))
const Alfiraseducationschooljeddahinternationalschooljisproject = lazy(() => import('./pages/projects/alfiraseducationschooljeddahinternationalschooljisproject'))
const Alphapharmakaecproject = lazy(() => import('./pages/projects/alphapharmakaecproject'))
const Hegraboutiquehotelforrcuproject = lazy(() => import('./pages/projects/hegraboutiquehotelforrcuproject'))
const Alkabeerexpansionprojectinkaecproject = lazy(() => import('./pages/projects/alkabeerexpansionprojectinkaecproject'))
const Thefilmstudiosandsupportingbuildingforrcuphase2project = lazy(() => import('./pages/projects/thefilmstudiosandsupportingbuildingforrcuphase2project'))
const Mbcstudiosstage23project = lazy(() => import('./pages/projects/mbcstudiosstage23project'))
const Onyxcontaineryardkaecproject = lazy(() => import('./pages/projects/onyxcontaineryardkaecproject'))
const Alimtiazschoolproject = lazy(() => import('./pages/projects/alimtiazschoolproject'))
const Altakamolalkhaleejiforfoodindustriesproject = lazy(() => import('./pages/projects/altakamolalkhaleejiforfoodindustriesproject'))
const Bellevuecentralspinemepworksproject = lazy(() => import('./pages/projects/bellevuecentralspinemepworksproject'))
const Amccinemasevenentertainmentproject = lazy(() => import('./pages/projects/amccinemasevenentertainmentproject'))
const Altadweerpm5project = lazy(() => import('./pages/projects/altadweerpm5project'))
const Baladyslaughterhousebuildingproject = lazy(() => import('./pages/projects/baladyslaughterhousebuildingproject'))
const Radicresidences01project = lazy(() => import('./pages/projects/radicresidences01project'))

// Admin pages
const AdminLogin = lazy(() => import('./pages/admin/Login'))
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'))
const AdminProjects = lazy(() => import('./pages/admin/Projects'))
const AdminJobApplications = lazy(() => import('./pages/admin/JobApplications'))
const AdminSubscriptions = lazy(() => import('./pages/admin/Subscriptions'))
const AdminBlog = lazy(() => import('./pages/admin/Blog'))
const AdminPageViews = lazy(() => import('./pages/admin/PageViews'))
const AdminDocuments = lazy(() => import('./pages/admin/Documents'))
const AdminVendors = lazy(() => import('./pages/admin/Vendors'))
const AdminClients = lazy(() => import('./pages/admin/Clients'))
const AdminUsers = lazy(() => import('./pages/admin/Users'))
const AdminSettings = lazy(() => import('./pages/admin/Settings'))
const AdminProfile = lazy(() => import('./pages/admin/Profile'))
const AdminSocialMedia = lazy(() => import('./pages/admin/SocialMedia'))

function App() {
  return (
    <LanguageProvider>
      <Router>
        <ScrollToTop />
        <Routes>
          {/* Public Routes with Header/Footer */}
          <Route path="/*" element={
            <div className="min-h-screen bg-white">
              <Header />
              <main className="flex-1">
                <Suspense fallback={<PageSkeleton />}>
                  <Routes>
                    <Route path="/" element={
                      <Suspense fallback={<HomeSkeleton />}>
                        <Home />
                      </Suspense>
                    } />
                    <Route path="/about" element={
                      <Suspense fallback={<AboutPageSkeleton />}>
                        <About />
                      </Suspense>
                    } />
                    <Route path="/services/building-infrastructure" element={
                      <Suspense fallback={<ServicePageSkeleton />}>
                        <BuildingInfrastructure />
                      </Suspense>
                    } />
                    <Route path="/services/design-engineering" element={
                      <Suspense fallback={<ServicePageSkeleton />}>
                        <DesignEngineering />
                      </Suspense>
                    } />
                    <Route path="/services/architectural-fitout" element={
                      <Suspense fallback={<ServicePageSkeleton />}>
                        <ArchitecturalFitout />
                      </Suspense>
                    } />
                    <Route path="/services/civil-works" element={
                      <Suspense fallback={<ServicePageSkeleton />}>
                        <CivilWorks />
                      </Suspense>
                    } />
                    <Route path="/services/mep-works" element={
                      <Suspense fallback={<ServicePageSkeleton />}>
                        <MEPWorks />
                      </Suspense>
                    } />
                    <Route path="/services/data-centers" element={
                      <Suspense fallback={<ServicePageSkeleton />}>
                        <DataCenters />
                      </Suspense>
                    } />
                    <Route path="/services/pricing" element={
                      <Suspense fallback={<ServicePageSkeleton />}>
                        <Pricing />
                      </Suspense>
                    } />
                    <Route path="/services/maintenance" element={
                      <Suspense fallback={<ServicePageSkeleton />}>
                        <Maintenance />
                      </Suspense>
                    } />
                    <Route path="/sectors" element={
                      <Suspense fallback={<ListingPageSkeleton />}>
                        <Sectors />
                      </Suspense>
                    } />
                    <Route path="/sectors/commercial" element={
                      <Suspense fallback={<SectorPageSkeleton />}>
                        <Commercial />
                      </Suspense>
                    } />
                    <Route path="/sectors/entertainment" element={
                      <Suspense fallback={<SectorPageSkeleton />}>
                        <Entertainment />
                      </Suspense>
                    } />
                    <Route path="/sectors/hotel" element={
                      <Suspense fallback={<SectorPageSkeleton />}>
                        <Hotel />
                      </Suspense>
                    } />
                    <Route path="/sectors/industrial" element={
                      <Suspense fallback={<SectorPageSkeleton />}>
                        <Industrial />
                      </Suspense>
                    } />
                    <Route path="/sectors/residential" element={
                      <Suspense fallback={<SectorPageSkeleton />}>
                        <Residential />
                      </Suspense>
                    } />
                    <Route path="/sectors/education" element={
                      <Suspense fallback={<SectorPageSkeleton />}>
                        <Education />
                      </Suspense>
                    } />
                    <Route path="/sectors/school" element={
                      <Suspense fallback={<SectorPageSkeleton />}>
                        <Education />
                      </Suspense>
                    } />
                    <Route path="/sectors/utilities" element={
                      <Suspense fallback={<SectorPageSkeleton />}>
                        <Utilities />
                      </Suspense>
                    } />
                    <Route path="/sectors/warehouse" element={
                      <Suspense fallback={<SectorPageSkeleton />}>
                        <Warehouse />
                      </Suspense>
                    } />
                    <Route path="/projects" element={
                      <Suspense fallback={<ProjectsPageSkeleton />}>
                        <Projects />
                      </Suspense>
                    } />
                    <Route path="/projects/al-esayi-beverages-khumrah-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Alesayibeverageskhumrahproject />
                      </Suspense>
                    } />
                    <Route path="/projects/al-esayi-beverages-phase-2-industrial-city-2-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Alesayibeveragesphase2industrialcity2project />
                      </Suspense>
                    } />
                    <Route path="/projects/madinah-depot-utilities-haramain-high-speed-railway-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Madinahdepotutilitiesharamainhighspeedrailwayproject />
                      </Suspense>
                    } />
                    <Route path="/projects/aatco-food-factory-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Aatcofoodfactoryproject />
                      </Suspense>
                    } />
                    <Route path="/projects/cigala-pharma-warehouse-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Cigalapharmawarehouseproject />
                      </Suspense>
                    } />
                    <Route path="/projects/ima-sigma-warehouse-international-maritime-agency-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Imasigmawarehouseinternationalmaritimeagencyproject />
                      </Suspense>
                    } />
                    <Route path="/projects/sheikh-ammar-youssef-naghi-villa-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Sheikhammaryousefnaghivillaproject />
                      </Suspense>
                    } />
                    <Route path="/projects/mini-depot-haramain-high-speed-railway-project-sabber" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Minidepotharamainhighspeedrailwayprojectsabber />
                      </Suspense>
                    } />
                    <Route path="/projects/work-base-01-haramain-high-speed-railway-jeddah-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Workbase01haramainhighspeedrailwayjeddahproject />
                      </Suspense>
                    } />
                    <Route path="/projects/depot-utilities-haramain-high-speed-railway-makkah-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Depotutilitiesharamainhighspeedrailwaymakkahproject />
                      </Suspense>
                    } />
                    <Route path="/projects/king-abudullah-economic-city-warehouse-park-rabigh-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Kingabudullaheconomiccitywarehouseparkrabighproject />
                      </Suspense>
                    } />
                    <Route path="/projects/empire-cinema-rashid-mall-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Empirecinemarashidmallproject />
                      </Suspense>
                    } />
                    <Route path="/projects/vox-cinemas-town-square-jamea-plaza-jeddah-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Voxcinemastownsqujameaplzajeddahproject />
                      </Suspense>
                    } />
                    <Route path="/projects/neom-sound-stage-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Neomsoundstageproject />
                      </Suspense>
                    } />
                    <Route path="/projects/neom-sound-stage-phase-2-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Neomsoundstagephase2project />
                      </Suspense>
                    } />
                    <Route path="/projects/sheikh-ahmed-bin-mahfouz-villa-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Sheikhahmedbinmahfouzvillaproject />
                      </Suspense>
                    } />
                    <Route path="/projects/delmon-expansion-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Delmonexpansionproject />
                      </Suspense>
                    } />
                    <Route path="/projects/al-tamimi-warehouse-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Altamimwarehouseproject />
                      </Suspense>
                    } />
                    <Route path="/projects/hhsr-fitouts-projects" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Hhsrfitoutsprojects />
                      </Suspense>
                    } />
                    <Route path="/projects/grand-cinema-obhur-mall-jeddah-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Grandcinemaobhurmalljeddahproject />
                      </Suspense>
                    } />
                    <Route path="/projects/prince-faisal-bin-mogren-taj-jeddah-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Princefaisalbinmogrentajjeddahproject />
                      </Suspense>
                    } />
                    <Route path="/projects/empire-cinema-othaim-mall-al-ahsa-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Empirecinemaothaimmallalahsaproject />
                      </Suspense>
                    } />
                    <Route path="/projects/al-yamama-window-tower-yanbu-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Alyamamawindowtoweryanbuproject />
                      </Suspense>
                    } />
                    <Route path="/projects/tamimi-kairwan-dammam-core-and-shell-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Tamimikairwandammamcoreandshellproject />
                      </Suspense>
                    } />
                    <Route path="/projects/the-film-studios-and-supporting-building-for-rcu-phase-1-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Thefilmstudiosandsupportingbuildingforrcuphase1project />
                      </Suspense>
                    } />
                    <Route path="/projects/rehabilitation-of-sheikh-saeed-abd-aldaim-mosque-for-rcu-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Rehabilitationofsheikhsaeedabdaldaimmosqueforrcuproject />
                      </Suspense>
                    } />
                    <Route path="/projects/empire-cinemas-at-othaim-mall-in-riyadh-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Empirecinemasatothaimmallinriyadhproject />
                      </Suspense>
                    } />
                    <Route path="/projects/grand-cinemas-at-almamsha-mall-in-al-ahsa-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Grandcinemasatalmamshamallinalahsaproject />
                      </Suspense>
                    } />
                    <Route path="/projects/fiber-cement-board-factory-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Fibercementboardfactoryproject />
                      </Suspense>
                    } />
                    <Route path="/projects/ayan-industrial-company-modulines-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Ayanindustrialcompanymodulinesproject />
                      </Suspense>
                    } />
                    <Route path="/projects/southern-pack-factory-kaec-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Southernpackfactorykaecproject />
                      </Suspense>
                    } />
                    <Route path="/projects/empire-cinema-at-al-rachid-mega-mall-in-madinah-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Empirecinemaatalrachidmegamallinmadinahproject />
                      </Suspense>
                    } />
                    <Route path="/projects/al-firas-education-school-jeddah-internation-school-jis-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Alfiraseducationschooljeddahinternationalschooljisproject />
                      </Suspense>
                    } />
                    <Route path="/projects/alpha-pharma-kaec-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Alphapharmakaecproject />
                      </Suspense>
                    } />
                    <Route path="/projects/hegra-boutique-hotel-rcu-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Hegraboutiquehotelforrcuproject />
                      </Suspense>
                    } />
                    <Route path="/projects/al-kabeer-expansion-kaec-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Alkabeerexpansionprojectinkaecproject />
                      </Suspense>
                    } />
                    <Route path="/projects/film-studios-supporting-building-rcu-phase2-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Thefilmstudiosandsupportingbuildingforrcuphase2project />
                      </Suspense>
                    } />
                    <Route path="/projects/mbc-studios-stage-2-3-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Mbcstudiosstage23project />
                      </Suspense>
                    } />
                    <Route path="/projects/onyx-container-yard-kaec-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Onyxcontaineryardkaecproject />
                      </Suspense>
                    } />
                    <Route path="/projects/al-imtiaz-school-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Alimtiazschoolproject />
                      </Suspense>
                    } />
                    <Route path="/projects/al-takamol-al-khaleeji-food-industries-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Altakamolalkhaleejiforfoodindustriesproject />
                      </Suspense>
                    } />
                    <Route path="/projects/bellevue-central-spine-mep-works-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Bellevuecentralspinemepworksproject />
                      </Suspense>
                    } />
                    <Route path="/projects/amc-cinema-seven-entertainment-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Amccinemasevenentertainmentproject />
                      </Suspense>
                    } />
                    <Route path="/projects/altadweer-pm5-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Altadweerpm5project />
                      </Suspense>
                    } />
                    <Route path="/projects/balady-slaughter-house-building-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Baladyslaughterhousebuildingproject />
                      </Suspense>
                    } />
                    <Route path="/projects/radic-residences-01-project" element={
                      <Suspense fallback={<SingleProjectSkeleton />}>
                        <Radicresidences01project />
                      </Suspense>
                    } />
                    <Route path="/contact" element={
                      <Suspense fallback={<ContactPageSkeleton />}>
                        <Contact />
                      </Suspense>
                    } />
                    <Route path="/documents" element={
                      <Suspense fallback={<CareerDocumentPageSkeleton />}>
                        <Documents />
                      </Suspense>
                    } />
                    <Route path="/blog" element={
                      <Suspense fallback={<BlogPageSkeleton />}>
                        <Blog />
                      </Suspense>
                    } />
                    {/* Individual blog post routes */}
                    <Route path="/blog/smart-cities-future-urban-infrastructure" element={
                      <Suspense fallback={<SingleBlogPostSkeleton />}>
                        <SmartCitiesFutureUrbanInfrastructure />
                      </Suspense>
                    } />
                    <Route path="/blog/sustainable-construction-building-tomorrow" element={
                      <Suspense fallback={<SingleBlogPostSkeleton />}>
                        <SustainableConstructionBuildingTomorrow />
                      </Suspense>
                    } />
                    <Route path="/blog/modern-bridge-engineering-concept-completion" element={
                      <Suspense fallback={<SingleBlogPostSkeleton />}>
                        <ModernBridgeEngineeringConceptCompletion />
                      </Suspense>
                    } />
                    <Route path="/blog/rise-prefabricated-construction" element={
                      <Suspense fallback={<SingleBlogPostSkeleton />}>
                        <RisePrefabricatedConstruction />
                      </Suspense>
                    } />
                    <Route path="/blog/safety-first-advanced-construction-protocols" element={
                      <Suspense fallback={<SingleBlogPostSkeleton />}>
                        <SafetyFirstAdvancedConstructionProtocols />
                      </Suspense>
                    } />
                    <Route path="/blog/green-building-materials-eco-friendly-alternatives" element={
                      <Suspense fallback={<SingleBlogPostSkeleton />}>
                        <GreenBuildingMaterialsEcoFriendlyAlternatives />
                      </Suspense>
                    } />
                    <Route path="/careers" element={
                      <Suspense fallback={<CareerDocumentPageSkeleton />}>
                        <Careers />
                      </Suspense>
                    } />
                    <Route path="/our-success-partners" element={
                      <Suspense fallback={<LogoGridPageSkeleton />}>
                        <OurSuccessPartners />
                      </Suspense>
                    } />
                    <Route path="/vendors" element={
                      <Suspense fallback={<LogoGridPageSkeleton />}>
                        <OurVendors />
                      </Suspense>
                    } />
                    <Route path="/organization-chart" element={
                      <Suspense fallback={<OrganizationChartSkeleton />}>
                        <OrganizationChart />
                      </Suspense>
                    } />
                    <Route path="/csr" element={
                      <Suspense fallback={<CSRSkeleton />}>
                        <CSR />
                      </Suspense>
                    } />
                    <Route path="/code-of-ethics" element={
                      <Suspense fallback={<PolicyPageSkeleton />}>
                        <CodeOfEthics />
                      </Suspense>
                    } />
                    <Route path="/hse" element={
                      <Suspense fallback={<PolicyPageSkeleton />}>
                        <HSE />
                      </Suspense>
                    } />
                    <Route path="/quality-management" element={
                      <Suspense fallback={<PolicyPageSkeleton />}>
                        <QualityManagement />
                      </Suspense>
                    } />
                    <Route path="/our-services" element={
                      <Suspense fallback={<ListingPageSkeleton />}>
                        <OurServices />
                      </Suspense>
                    } />
                    <Route path="/industries-sectors" element={
                      <Suspense fallback={<ListingPageSkeleton />}>
                        <IndustriesSectors />
                      </Suspense>
                    } />
                    <Route path="/clients" element={
                      <Suspense fallback={<LogoGridPageSkeleton />}>
                        <Clients />
                      </Suspense>
                    } />
                    <Route path="/privacy-policy" element={
                      <Suspense fallback={<LegalPageSkeleton />}>
                        <PrivacyPolicy />
                      </Suspense>
                    } />
                    <Route path="/terms-of-service" element={
                      <Suspense fallback={<LegalPageSkeleton />}>
                        <TermsOfService />
                      </Suspense>
                    } />
                    <Route path="/cookie-policy" element={
                      <Suspense fallback={<LegalPageSkeleton />}>
                        <CookiePolicy />
                      </Suspense>
                    } />
                    <Route path="/site-map" element={
                      <Suspense fallback={<LegalPageSkeleton />}>
                        <SiteMap />
                      </Suspense>
                    } />
                  </Routes>
                </Suspense>
              </main>
              <Footer />
              <GoToTop />
              <ContactWidget />
            </div>
          } />

          {/* Admin Routes without Header/Footer */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={
            <Suspense fallback={<AdminSkeleton />}>
              <AdminDashboard />
            </Suspense>
          } />
          <Route path="/admin/projects" element={
            <Suspense fallback={<AdminSkeleton />}>
              <AdminProjects />
            </Suspense>
          } />
          <Route path="/admin/contact-submissions" element={
            <Suspense fallback={<AdminSkeleton />}>
              <AdminJobApplications />
            </Suspense>
          } />
          <Route path="/admin/subscriptions" element={
            <Suspense fallback={<AdminSkeleton />}>
              <AdminSubscriptions />
            </Suspense>
          } />
          <Route path="/admin/blog" element={
            <Suspense fallback={<AdminSkeleton />}>
              <AdminBlog />
            </Suspense>
          } />
          <Route path="/admin/page-views" element={
            <Suspense fallback={<AdminSkeleton />}>
              <AdminPageViews />
            </Suspense>
          } />
          <Route path="/admin/documents" element={
            <Suspense fallback={<AdminSkeleton />}>
              <AdminDocuments />
            </Suspense>
          } />
          <Route path="/admin/vendors" element={
            <Suspense fallback={<AdminSkeleton />}>
              <AdminVendors />
            </Suspense>
          } />
          <Route path="/admin/clients" element={
            <Suspense fallback={<AdminSkeleton />}>
              <AdminClients />
            </Suspense>
          } />
          <Route path="/admin/users" element={
            <Suspense fallback={<AdminSkeleton />}>
              <AdminUsers />
            </Suspense>
          } />
          <Route path="/admin/settings" element={
            <Suspense fallback={<AdminSkeleton />}>
              <AdminSettings />
            </Suspense>
          } />
          <Route path="/admin/profile" element={
            <Suspense fallback={<AdminSkeleton />}>
              <AdminProfile />
            </Suspense>
          } />
          <Route path="/admin/social-media" element={
            <Suspense fallback={<AdminSkeleton />}>
              <AdminSocialMedia />
            </Suspense>
          } />
        </Routes>
      </Router>
    </LanguageProvider>
  )
}

export default App
